import type { Axis, AxisName, Layout, LayoutAxis } from "plotly.js";
import type { FormatDescriptor } from "./bindings/FormatDescriptor.js";
import type { FormatRegistry } from "./bindings/metadata.js";
import { tableMetadata } from "./constants.js";
import { axis, axisPresets, calculateDomains } from "./layoutHelpers.js";
import { registryMetadata } from "./metadata.js";
import type { TableUserConfig } from "./userPlotConfig.js";

export type ExtendedLayout = Partial<Layout> & {
	[key: `yaxis${number}`]: Partial<Axis>;
};

class TitleGetter {
	private metadata: FormatDescriptor;
	constructor(tableId: number) {
		const metadata = registryMetadata[tableId];
		if (!metadata) {
			throw new Error(`Table ID ${tableId} not found in registry metadata`);
		}
		this.metadata = metadata;
	}
	get(fieldName: string): string {
		const field = this.metadata.fields.find((f) => f.name === fieldName);
		if (!field) return fieldName;
		const displayName = field.label || field.description || field.name;
		if (field.unit) {
			return `${displayName} (${field.unit})`;
		}
		return displayName;
	}
}

export interface Stations {
	[key: string]: FormatRegistry;
}
/** panel: 0-based row the field is drawn in (0 = bottom), matching calculateDomains */
export type FieldAxisMap = Map<
	string,
	{ axisName: string; color: string; panel?: number }
>;
export interface LayoutWithMap {
	layout: ExtendedLayout;
	fieldAxisMap: FieldAxisMap;
	/** Number of rows in use, when it differs from tableMetadata (custom layouts) */
	effectiveDomains?: number;
}

const axisKeyFor = (axisName: string): string =>
	axisName === "y" ? "yaxis" : `yaxis${axisName.slice(1)}`;

/**
 * Record each field's row. Overlay axes are drawn in the row of the axis they
 * overlay, whatever domain they were declared with.
 */
function assignPanels(tableId: number, result: LayoutWithMap): LayoutWithMap {
	const numDomains = tableMetadata[tableId]?.numDomains ?? 0;
	const domains = calculateDomains(numDomains);
	const axes = result.layout as Record<string, Partial<LayoutAxis> | undefined>;
	for (const entry of result.fieldAxisMap.values()) {
		const axis = axes[axisKeyFor(entry.axisName)];
		const primary = axis?.overlaying
			? axes[axisKeyFor(axis.overlaying)]
			: axis;
		const domain = primary?.domain;
		const panel = domain
			? domains.findIndex(
					([s, e]) =>
						Math.abs(s - domain[0]) < 1e-4 && Math.abs(e - domain[1]) < 1e-4,
				)
			: -1;
		entry.panel = panel >= 0 ? panel : 0;
	}
	return result;
}

function createTableId1Layout(): LayoutWithMap {
	const table_id = 1;
	const domains = calculateDomains(tableMetadata[table_id].numDomains);
	const title = new TitleGetter(table_id);
	const fieldAxisMap: FieldAxisMap = new Map();
	const addAxis = (
		axisKey: string,
		fieldName: string,
		color: string,
		domain: [number, number],
		overlayingAxis?: AxisName,
		options?: Partial<Axis>,
	): Partial<Axis> => {
		const axisName = axisKey === "yaxis" ? "y" : `y${axisKey.slice(5)}`;
		fieldAxisMap.set(fieldName, { axisName, color });
		return axis(title, color, domain, fieldName, overlayingAxis, options);
	};
	return {
		layout: {
			showlegend: true,
			xaxis: { title: { text: "Date" } },
			yaxis: addAxis("yaxis", "BattV_Min", "#0173B2", domains[0]),
			yaxis2: addAxis("yaxis2", "PTemp_C_Min", "#DE8F05", domains[1]),
			yaxis3: addAxis("yaxis3", "AirTC_Avg", "#029E73", domains[0], "y"),
			yaxis4: addAxis(
				"yaxis4",
				"RH_Avg",
				"#CC78BC",
				domains[2],
				undefined,
				axisPresets.percentage,
			),
			yaxis5: addAxis("yaxis5", "SW_in_Avg", "#CA9161", domains[1], "y2"),
			yaxis6: addAxis(
				"yaxis6",
				"Apogee_barometer",
				"#949494",
				domains[2],
				"y4",
			),
			yaxis7: addAxis("yaxis7", "WS_ms_S_WVT", "#0173B2", domains[3]),
			yaxis8: addAxis(
				"yaxis8",
				"WindDir_D1_WVT",
				"#DE8F05",
				domains[3],
				"y7",
				axisPresets.windDirection,
			),
		},
		fieldAxisMap,
	};
}

function createTableId2Layout(): LayoutWithMap {
	const table_id = 2;
	const domains = calculateDomains(tableMetadata[table_id].numDomains);
	const title = new TitleGetter(table_id);
	const fieldAxisMap: FieldAxisMap = new Map();
	const addAxis = (
		axisKey: string,
		fieldName: string,
		color: string,
		domain: [number, number],
		overlayingAxis?: AxisName,
		options?: Partial<Axis>,
	): Partial<Axis> => {
		const axisName = axisKey === "yaxis" ? "y" : `y${axisKey.slice(5)}`;
		fieldAxisMap.set(fieldName, { axisName, color });
		return axis(title, color, domain, fieldName, overlayingAxis, options);
	};
	const addFieldsToAxis = (
		axisName: string,
		fields: Array<{ name: string; color: string }>,
	): void => {
		for (const field of fields) {
			const yAxisName = axisName === "yaxis" ? "y" : `y${axisName.slice(5)}`;
			fieldAxisMap.set(field.name, { axisName: yAxisName, color: field.color });
		}
	};
	return {
		layout: {
			showlegend: true,
			xaxis: { title: { text: "Date" } },
			yaxis: ((): Partial<Axis> => {
				addFieldsToAxis("yaxis", [
					{ name: "T109_C_1", color: "#440154" }, // Dark purple
					{ name: "T109_C_2", color: "#472c7a" }, // Medium purple
					{ name: "T109_C_3", color: "#3b528b" }, // Blue-purple
					{ name: "T109_C_4", color: "#2c738e" }, // Blue
					{ name: "T109_C_5", color: "#21908c" }, // Teal
					{ name: "T109_C_6", color: "#27ad81" }, // Green-teal
					{ name: "T109_C_7", color: "#5ec962" }, // Light green
					{ name: "T109_C_8", color: "#b5de2b" }, // Yellow-green
					{ name: "T109_C_9", color: "#e6e418" }, // Yellow
					{ name: "T109_C_10", color: "#fde724" }, // Bright yellow
				]);
				return axis(title, "#440154", domains[0], "T109_C_1");
			})(),
			yaxis2: addAxis("yaxis2", "BattV", "#0173B2", domains[1]),
			yaxis3: addAxis("yaxis3", "PTemp_C", "#DE8F05", domains[1], "y2"),
			yaxis4: addAxis("yaxis4", "TRHData_1", "#029E73", domains[2]),
			yaxis5: addAxis(
				"yaxis5",
				"TRHData_2",
				"#CC78BC",
				domains[2],
				"y4",
				axisPresets.percentage,
			),
			// yaxis6: ((): Partial<Axis> => {
			// 	addFieldsToAxis("yaxis6", [
			// 		{ name: "Net_rad_1", color: "#ffc107" },
			// 		{ name: "Net_rad_2", color: "#ff6f00" },
			// 		{ name: "Net_rad_3", color: "#f57c00" },
			// 		{ name: "Net_rad_4", color: "#e65100" },
			// 	]);
			// 	return axis(title, "#ffc107", domains[5], "Net_rad_1");
			// })(),
			yaxis7: addAxis("yaxis7", "VWC", "#0173B2", domains[3]),
			yaxis8: addAxis("yaxis8", "VWC_2", "#DE8F05", domains[3], "y7"),
			yaxis9: addAxis("yaxis9", "Davis_winspd_S_WVT", "#CA9161", domains[4]),
			yaxis10: addAxis(
				"yaxis10",
				"Wind_dir_D1_WVT",
				"#949494",
				domains[4],
				"y9",
				{ ...axisPresets.windDirection },
			),
		},
		fieldAxisMap,
	};
}

function createTableId3Layout(): LayoutWithMap {
	const table_id = 3;
	const domains = calculateDomains(tableMetadata[table_id].numDomains);
	const title = new TitleGetter(table_id);
	const fieldAxisMap: FieldAxisMap = new Map();
	const addAxis = (
		axisKey: string,
		fieldName: string,
		color: string,
		domain: [number, number],
		overlayingAxis?: AxisName,
		options?: Partial<Axis>,
	): Partial<Axis> => {
		const axisName = axisKey === "yaxis" ? "y" : `y${axisKey.slice(5)}`;
		fieldAxisMap.set(fieldName, { axisName, color });
		return axis(title, color, domain, fieldName, overlayingAxis, options);
	};
	const addFieldsToAxis = (
		axisName: string,
		fields: Array<{ name: string; color: string }>,
	): void => {
		for (const field of fields) {
			const yAxisName = axisName === "yaxis" ? "y" : `y${axisName.slice(5)}`;
			fieldAxisMap.set(field.name, { axisName: yAxisName, color: field.color });
		}
	};
	return {
		layout: {
			showlegend: true,
			xaxis: { title: { text: "Date" } },
			yaxis: ((): Partial<Axis> => {
				addFieldsToAxis("yaxis", [
					{ name: "T109_C_1", color: "#440154" }, // Dark purple
					{ name: "T109_C_2", color: "#472c7a" }, // Medium purple
					{ name: "T109_C_3", color: "#3b528b" }, // Blue-purple
					{ name: "T109_C_4", color: "#2c738e" }, // Blue
					{ name: "T109_C_5", color: "#21908c" }, // Teal
					{ name: "T109_C_6", color: "#27ad81" }, // Green-teal
					{ name: "T109_C_7", color: "#5ec962" }, // Light green
					{ name: "T109_C_8", color: "#b5de2b" }, // Yellow-green
					{ name: "T109_C_9", color: "#e6e418" }, // Yellow
					{ name: "T109_C_10", color: "#fde724" }, // Bright yellow
				]);
				return axis(title, "#440154", domains[0], "T109_C_1");
			})(),
			yaxis2: addAxis("yaxis2", "BattV", "#0173B2", domains[1]),
			yaxis3: addAxis("yaxis3", "PTemp_C", "#DE8F05", domains[1], "y2"),
			yaxis4: addAxis("yaxis4", "TRHData_1", "#029E73", domains[2]),
			yaxis5: addAxis(
				"yaxis5",
				"TRHData_2",
				"#CC78BC",
				domains[2],
				"y4",
				axisPresets.percentage,
			),
			yaxis6: addAxis("yaxis6", "VWC", "#0173B2", domains[3]),
			yaxis7: addAxis("yaxis7", "VWC_2", "#DE8F05", domains[3], "y6"),
			yaxis8: addAxis("yaxis8", "Davis_winspd_S_WVT", "#CA9161", domains[4]),
			yaxis9: addAxis(
				"yaxis9",
				"Wind_dir_D1_WVT",
				"#949494",
				domains[4],
				"y8",
				{ ...axisPresets.windDirection },
			),
		},
		fieldAxisMap,
	};
}

function createTableId4Layout(): LayoutWithMap {
	const table_id = 4;
	const domains = calculateDomains(tableMetadata[table_id].numDomains);
	const title = new TitleGetter(table_id);
	const fieldAxisMap: FieldAxisMap = new Map();
	const addAxis = (
		axisKey: string,
		fieldName: string,
		color: string,
		domain: [number, number],
		overlayingAxis?: AxisName,
		options?: Partial<Axis>,
	): Partial<Axis> => {
		const axisName = axisKey === "yaxis" ? "y" : `y${axisKey.slice(5)}`;
		fieldAxisMap.set(fieldName, { axisName, color });
		return axis(title, color, domain, fieldName, overlayingAxis, options);
	};
	const addFieldsToAxis = (
		axisName: string,
		fields: Array<{ name: string; color: string }>,
	): void => {
		for (const field of fields) {
			const yAxisName = axisName === "yaxis" ? "y" : `y${axisName.slice(5)}`;
			fieldAxisMap.set(field.name, { axisName: yAxisName, color: field.color });
		}
	};

	return {
		layout: {
			showlegend: true,
			// Anchor to the bottom subplot (domains[0]); Plotly defaults to "y", which is the top one here
			xaxis: { title: { text: "Date" }, anchor: "y9" },
			yaxis: addAxis("yaxis", "BattV_Avg", "#0173B2", domains[6]),
			yaxis2: addAxis("yaxis2", "PTemp_C_Avg", "#DE8F05", domains[6], "y"),
			yaxis3: ((): Partial<Axis> => {
				// Use one shared axis so SW in/out are directly comparable for Cass.
				addFieldsToAxis("yaxis3", [
					{ name: "IncomingSW_Avg", color: "#029E73" },
					{ name: "OutgoingSW_Avg", color: "#ECE2F0" },
				]);
				return axis(title, "#029E73", domains[2], "IncomingSW_Avg", undefined, {
					title: { text: "Shortwave Radiation (W/m²)" },
				});
			})(),
			yaxis5: ((): Partial<Axis> => {
				// Shared axis for both air temperatures at same scale
				addFieldsToAxis("yaxis5", [
					{ name: "T107_C_airT_50cm_Avg", color: "#0173B2" },
					{ name: "Upper_AirTemp_Avg", color: "#DE8F05" },
				]);
				return axis(title, "#0173B2", domains[1], "T107_C_airT_50cm_Avg");
			})(),
			yaxis7: addAxis(
				"yaxis7",
				"RH_Avg",
				"#CC78BC",
				domains[5],
				undefined,
				axisPresets.percentage,
			),
			yaxis8: addAxis("yaxis8", "AirPress_mbar", "#029E73", domains[3], "y7"),
			yaxis9: ((): Partial<Axis> => {
				// Shared axis for both soil temperatures at same scale
				addFieldsToAxis("yaxis9", [
					{ name: "SoilTemp_C_2cm_Avg", color: "#CA9161" },
					{ name: "soil_Temp_C_30cm_Avg", color: "#949494" },
				]);
				return axis(title, "#CA9161", domains[0], "SoilTemp_C_2cm_Avg");
			})(),
			yaxis11: addAxis("yaxis11", "Rain_mm_Tot", "#0173B2", domains[4]),
			yaxis12: addAxis(
				"yaxis12",
				"Rain_gauge_Temp_C_Avg",
				"#DE8F05",
				domains[4],
				"y11",
			),
			yaxis13: addAxis("yaxis13", "Wind_speed_WVc_1", "#CC78BC", domains[3]),
			yaxis14: addAxis(
				"yaxis14",
				"Wind_direction_WVc_2",
				"#8e24aa",
				domains[3],
				"y13",
				axisPresets.windDirection,
			),
		},
		fieldAxisMap,
	};
}

export function getLayoutForTableId(tableId: number): LayoutWithMap {
	switch (tableId) {
		case 1:
			return assignPanels(1, createTableId1Layout());
		case 2:
			return assignPanels(2, createTableId2Layout());
		case 3:
			return assignPanels(3, createTableId3Layout());
		case 4:
			return assignPanels(4, createTableId4Layout());
		default:
			return assignPanels(1, createTableId1Layout());
	}
}

/** Default field configuration: every field visible, in its hand-tuned row */
export function getDefaultTableConfig(tableId: number): TableUserConfig {
	const config: TableUserConfig = {};
	for (const [fieldName, { panel }] of getLayoutForTableId(tableId)
		.fieldAxisMap) {
		config[fieldName] = { visible: true, panel: panel ?? 0 };
	}
	return config;
}

function configMatchesDefault(
	tableId: number,
	config: TableUserConfig,
): boolean {
	return Object.entries(getDefaultTableConfig(tableId)).every(
		([name, defaults]) =>
			(config[name]?.visible ?? true) &&
			(config[name]?.panel ?? defaults.panel) === defaults.panel,
	);
}

/**
 * Layout for a (possibly absent) user field config. Unchanged configs, including
 * after "Reset to defaults", keep the hand-tuned layout.
 */
export function layoutForConfig(
	tableId: number,
	config: TableUserConfig | null,
): LayoutWithMap {
	if (!config || configMatchesDefault(tableId, config)) {
		return getLayoutForTableId(tableId);
	}
	return buildLayoutFromUserConfig(tableId, config);
}

/**
 * Build a layout from a user field config: hidden fields are dropped, empty rows
 * removed and rows re-spaced. Fields that shared an axis in the hand-tuned layout
 * keep sharing it, and each axis keeps its original title and presets.
 */
export function buildLayoutFromUserConfig(
	tableId: number,
	userConfig: TableUserConfig,
): LayoutWithMap {
	const { layout: defaultLayout, fieldAxisMap: defaultMap } =
		getLayoutForTableId(tableId);
	const defaultAxes = defaultLayout as Record<string, Partial<LayoutAxis>>;

	type Entry = { name: string; color: string; axisName: string; panel: number };
	const entries: Entry[] = [];
	for (const [name, { axisName, color, panel }] of defaultMap) {
		const fc = userConfig[name];
		if (fc && !fc.visible) continue;
		entries.push({ name, color, axisName, panel: fc?.panel ?? panel ?? 0 });
	}

	const layout: ExtendedLayout = {
		showlegend: true,
		xaxis: { title: { text: "Date" } },
	};
	const fieldAxisMap: FieldAxisMap = new Map();
	const panels = [...new Set(entries.map((e) => e.panel))].sort(
		(a, b) => a - b,
	);
	const domains = calculateDomains(panels.length);
	const axes = layout as Record<string, Partial<LayoutAxis>>;

	let axisIndex = 1;
	panels.forEach((panel, domainIndex) => {
		let primary: AxisName | undefined;
		// One axis per default axis within the row, so shared axes stay shared
		const byDefaultAxis = new Map<string, Entry[]>();
		for (const entry of entries.filter((e) => e.panel === panel)) {
			const group = byDefaultAxis.get(entry.axisName) ?? [];
			group.push(entry);
			byDefaultAxis.set(entry.axisName, group);
		}
		for (const [defaultAxisName, group] of byDefaultAxis) {
			const axisName = (axisIndex === 1 ? "y" : `y${axisIndex}`) as AxisName;
			// Start from the hand-tuned axis (title, presets), then re-place it
			const axis: Partial<LayoutAxis> = {
				...defaultAxes[axisKeyFor(defaultAxisName)],
				domain: domains[domainIndex],
			};
			delete axis.overlaying;
			delete axis.side;
			if (primary) {
				axis.overlaying = primary;
				axis.side = "right";
				// Plotly 4 defaults overlaying axes to "sync" ticks; keep independent ticks
				if (axis.dtick === undefined && axis.tickvals === undefined) {
					axis.tickmode ??= "auto";
				}
			} else {
				primary = axisName;
			}
			axes[axisKeyFor(axisName)] = axis;
			for (const { name, color } of group) {
				fieldAxisMap.set(name, { axisName, color, panel });
			}
			axisIndex++;
		}
	});

	return { layout, fieldAxisMap, effectiveDomains: panels.length };
}

const defaultLayoutWithMap = createTableId1Layout();
export const layout: ExtendedLayout = defaultLayoutWithMap.layout;
